import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'it';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    calendar: 'Calendar',
    concerts: 'Concerts',
    artistic_projects: 'Artistic Projects',
    annual_plan: 'Annual Plan',
    music_catalog: 'Music Catalog',
    repertoire: 'Repertoire',
    contacts: 'Contacts',
    venues: 'Venues',
    contracts: 'Contracts',
    templates: 'Contract Templates',
    tasks: 'Tasks',
    my_fees: 'My Fees',
    revenue_shares: 'Collaborations',
    expenses: 'Expenses',
    media_library: 'Media Library',
    cost_calculator: 'Cost Calculator',
    tour_calculator: 'Tour Calculator',
    analytics: 'Analytics',
    ai_expert: 'AI Music Expert',
    tutorials: 'Tutorials',
    user_management: 'User Management',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    fan_crm: 'Fan CRM',
    fan_analytics: 'Fan Analytics',
    products: 'Products',
    integrations: 'Integrations',
    help: 'Help',
    help_center: 'Help Center',
    
    // Menu sections
    section_core: 'Main',
    section_management: 'Management',
    section_fan_marketing: 'Fan & Marketing',
    section_financial: 'Financial',
    section_tools: 'Tools',
    section_account: 'Account',
    section_admin: 'Administration',
    payment_analytics: 'Payment Analytics',
    cms_admin: 'CMS Management',
    blog_admin: 'Blog Management',
    platform_updates_admin: 'Platform Updates',
    auto_comms_admin: 'Auto Communications',
    landing_images_admin: 'Landing Images',
    landing_settings_admin: 'Landing Settings',
    
    // Admin users management
    admin_users_title: 'Registered Users',
    admin_users_description: 'Manage all platform users',
    search_users: 'Search by email or organization...',
    filter_by_plan: 'Filter by plan',
    all_plans: 'All Plans',
    no_plan: 'No Plan',
    no_organization: 'No organization',
    no_users_found: 'No users found',
    no_users_registered: 'No users registered',
    total_users: 'Total users',
    showing_filtered: 'showing',
    registered_at: 'Registered',
    confirm_delete_user: 'Delete User',
    delete_user_warning: 'Are you sure you want to delete the user',
    delete_user_permanent: 'This action is irreversible. All user data will be permanently deleted.',
    
    // Venues
    total_venues: 'Total Venues',
    with_piano: 'With Piano',
    avg_capacity: 'Avg Capacity',
    managed_locations: 'Managed locations',
    piano_available: 'Piano available',
    average_seats: 'Average seats',
    all_venues: 'All Venues',
    no_venues_yet: 'No venues yet',
    add_first_venue_desc: 'Add your first venue to start organizing concerts',
    add_first_venue: 'Add Your First Venue',
    add_venue: 'Add Venue',
    delete_venue: 'Delete Venue',
    delete_venue_confirm: 'Are you sure you want to delete this venue? This action cannot be undone.',
    venue_deleted: 'Venue deleted successfully',
    venue_delete_error: 'Failed to delete venue',
    capacity_label: 'Capacity',
    internal_manager: 'Internal Manager',
    venue_contact: 'Venue Contact',
    commission_label: 'Commission',
    venue_name: 'Venue Name',
    venue_type: 'Venue Type',
    select_venue_type: 'Select venue type',
    select_manager: 'Select manager',
    select_venue_contact: 'Select venue contact',
    ticket_commission: 'Ticket Commission %',
    typical_rental_cost: 'Typical Rental Cost (€)',
    piano_availability: 'Piano Availability',
    select_piano: 'Select piano availability',
    piano_none: 'None',
    piano_acoustic: 'Acoustic',
    piano_digital: 'Digital',
    venue_created: 'Venue created successfully',
    venue_updated: 'Venue updated successfully',
    venue_create_error: 'Failed to create venue',
    venue_update_error: 'Failed to update venue',
    add_new_venue: 'Add New Venue',
    edit_venue: 'Edit Venue',
    update_venue: 'Update Venue',
    create_venue: 'Create Venue',
    filter_by_region: 'Filter by region',
    all_regions: 'All Regions',
    all_countries: 'All Countries',
    additional_notes: 'Additional notes about the venue',
    search_address: 'Search Address (Auto-fill)',
    search_address_hint: 'Search an address to automatically fill the fields below',
    address_filled: 'Address filled automatically',
    showing_venues: 'Showing',
    of_venues: 'of',
    venues_label: 'venues',
    follow_up_label: 'Follow-up',
    
    // Common
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    loading: 'Loading...',
    noData: 'No data available',
    
    // Dashboard
    welcomeBack: 'Welcome back',
    totalRevenue: 'Total Revenue',
    totalExpenses: 'Total Expenses',
    upcomingConcerts: 'Upcoming Concerts',
    pendingTasks: 'Pending Tasks',
    dashboard_overview: "Here's your music business overview for",
    unlimited_access: 'Unlimited access',
    custom_view: 'Custom View',
    multi_role_view: 'Multi-Role View',
    no_completed_concerts: 'No completed concerts in',
    as_booking_manager: 'As Booking Manager',
    as_performer: 'As Performer',
    revenue: 'Revenue',
    expenses_label: 'Expenses',
    booking_fees: 'Booking Fees',
    revenue_shares_label: 'Revenue Shares',
    net_income: 'Net Income',
    concerts_organized: 'concerts organized',
    concerts_performed: 'concerts performed',
    operating_costs: 'Operating costs',
    performance_costs: 'Performance costs',
    commission_income: 'Commission income + monthly retainers',
    managed_for_artists: 'Managed for artists',
    after_expenses: 'After expenses',
    combined_total: 'Combined Total',
    all_costs: 'All costs',
    paid_to_collaborators: 'Paid to collaborators',
    ytd_spending: 'Year-to-date spending',
    total_after_expenses: 'Total after all expenses',
    open_tasks: 'Open Tasks',
    view_all: 'View All',
    no_open_tasks: "No open tasks - you're all caught up!",
    in_progress: 'In Progress',
    no_upcoming_concerts: 'No upcoming confirmed concerts',
    this_month: 'This Month',
    projected_revenue: 'Projected Revenue',
    urgent_tasks: 'Urgent Tasks',
    quick_actions: 'Quick Actions',
    add_concert: 'Add Concert',
    add_expense: 'Add Expense',
    add_task: 'Add Task',
    import_data: 'Import Data',
    organizer_label: 'Organizer',
    performer_label: 'Performer',
    est_cost: 'Est. Cost',
    expected_revenue: 'Expected Revenue',
    my_booking_fees: 'My Booking Fees',
    commission_retainers: 'Commission income + retainers',
    net_income_for_artists: 'Net income for Artists',
    after_all_expenses: 'After all expenses',
    of_concerts: 'of',
    concerts_label: 'concerts',
    role_breakdown_toggle: 'View detail by role',
    role_breakdown_hide: 'Hide detail by role',
    completed_concerts_short: 'Completed concerts',
    setup_guide: 'Setup guide',
    
    // Concerts Page
    concerts_title: 'Concerts',
    concerts_description: 'Manage your concert schedule and details',
    search_concerts: 'Search concerts...',
    all_years: 'All Years',
    all_types: 'All Types',
    all_status: 'All Status',
    fixed_price: 'Fixed Price',
    ticket_sales: 'Ticket Sales',
    planned: 'Planned',
    tentative: 'Planned', // legacy alias
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    total_concerts: 'Total Concerts',
    showing: 'Showing',
    no_concerts_yet: 'No concerts yet',
    add_first_concert_desc: 'Add your first concert to get started',
    add_first_concert: 'Add Your First Concert',
    
    // Calendar page
    calendar_title: 'Concert Calendar',
    calendar_description: 'View and manage your concerts and events',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Agenda',
    today: 'Today',
    day_details: 'Day Details',
    all_projects: 'All Projects',
    no_concerts_this_day: 'No concerts on this day',
    select_day_to_view_details: 'Select a day to view details',
    day_total: 'Day total',
    not_available: 'Not Available',
    moved_to: 'moved to',
    concert_confirmed: 'Concert confirmed',
    concert_cancelled: 'Concert cancelled',
    concert_saved: 'Concert saved',
    events: 'events',
    my_booking_fee: 'My Booking Fee',
    venue_commission: 'Venue Commission',
    revenues: 'Revenues',
    direct_expenses: 'Direct Expenses',
    ticket_sales_label: 'Ticket Sales',
    performance_fee: 'Performance Fee',
    merchandise: 'Merchandise',
    travel: 'Travel',
    accommodation: 'Accommodation',
    piano_rental: 'Piano Rental',
    booking_commission: 'Booking Commission',
    other: 'Other',
    collaborators: 'Collaborators',
    manage_collaborators: 'Manage Collaborators',
    duplicate: 'Duplicate',
    delete_concert: 'Delete Concert',
    delete_concert_confirm: 'Are you sure you want to delete this concert? This action cannot be undone.',
    estimated_costs: 'Estimated Costs',
    total_expected: 'Total Expected',
    expected_label: 'Expected',
    net_income_after_fee: 'Net Income (after my fee)',
    my_net_income: 'My Net Income',
    concert_deleted: 'Concert deleted successfully',
    concert_delete_permission: 'You do not have permission to delete this concert.',
    concert_duplicated: 'Concert duplicated successfully!',
    edit_now: 'Edit Now',
    failed_duplicate: 'Failed to duplicate concert',
    refresh: 'Refresh',
    refreshing: 'Refreshing concerts data...',
    export_csv: 'Export CSV',
    no_concerts_export: 'No concerts to export',
    concerts_exported: 'Concerts exported successfully',
    failed_load_concerts: 'Failed to load concerts. Please try again.',
    
    // Contacts Page
    contacts_title: 'Contacts',
    contacts_description: 'Manage your professional network',
    add_contact: 'Add Contact',
    search_contacts: 'Search contacts...',
    all_roles: 'All Roles',
    all_followups: 'All Follow-ups',
    contact_role_artist: 'Artist',
    contact_role_promoter: 'Promoter',
    contact_role_venue_manager: 'Venue Manager',
    contact_role_agent: 'Agent',
    contact_role_other: 'Other',
    upcoming: 'Upcoming',
    overdue: 'Overdue',
    no_followup_set: 'No Follow-up Set',
    show_inactive: 'Show inactive contacts',
    no_contacts_found: 'No contacts found',
    adjust_filters: 'Try adjusting your filters',
    add_first_contact: 'Add your first contact to get started',
    contact_directory: 'Contact Directory',
    contacts_tab_team: 'Team',
    contacts_tab_directory: 'Directory',
    contacts_team_member: 'Team Member',
    contacts_team_description: 'Members of your organization who collaborate on the platform',
    contacts_directory_description: 'External contacts: promoters, venues, agents and more',
    active: 'Active',
    inactive: 'Inactive',
    due_soon: 'Due soon',
    generate_followup_task: 'Generate Follow-up Task',
    deactivate_title: 'Are you sure?',
    deactivate_description: 'This will deactivate the contact',
    deactivate_note: 'They will no longer appear in the default view but can be reactivated later',
    deactivate: 'Deactivate',
    contact_deactivated: 'Contact deactivated successfully',
    fee_label: 'fee',
    no_data_export: 'No data to export',
    warning_dependencies: 'Warning: This contact has dependencies',
    
    // Help Center
    help_description: 'Step-by-step guides, documentation and resources to get the most out of Maestro Music Manager',
    quick_start: 'Quick Start',
    documentation: 'Documentation',
    video_tutorials: 'Video Tutorials',
    role_guides: 'Role Guides',
    faq: 'FAQ',
    start_here: 'Start here',
    start_here_description: 'Choose the path that best fits your role to get started quickly',
    for_artists: 'For Artists',
    for_artists_description: 'Track your concerts, monitor earnings and manage revenue shares',
    start_artist_guide: 'Start Artist Guide',
    for_booking_managers: 'For Booking Managers',
    for_booking_managers_description: 'Organize events, calculate commissions and manage artists',
    start_bm_guide: 'Start Booking Manager Guide',
    for_venue_managers: 'For Venue Managers',
    for_venue_managers_description: 'Register venues, manage availability and collaborate with organizers',
    start_vm_guide: 'Start Venue Manager Guide',
    ai_assistant: 'AI Assistant',
    ai_assistant_description: 'Ask questions about your data and get immediate answers',
    discover_ai: 'Discover AI Assistant',
    
    // Documentation tabs
    doc_description: 'Detailed documentation on all Maestro Music Manager features',
    doc_concerts: 'Concerts',
    doc_contacts: 'Contacts',
    doc_revenue_shares: 'Revenue Shares',
    doc_analytics: 'Analytics',
    doc_mobile: 'Mobile',
    
    // Concerts documentation
    doc_concert_management: 'Concert Management',
    doc_concert_types: 'Concert Types',
    doc_concert_types_content: 'Fixed Fee: The artist receives a guaranteed fixed fee regardless of ticket sales. Ideal for venues with predictable budgets. Ticket Sales: Revenue is based on actual sales. Enter ticket price and capacity. The system calculates revenue from the percentage of tickets sold.',
    doc_fixed_fee: 'Fixed Fee',
    doc_fixed_fee_desc: 'The artist receives a guaranteed fixed fee regardless of ticket sales. Ideal for venues with predictable budgets.',
    doc_ticket_sales: 'Ticket Sales',
    doc_ticket_sales_desc: 'Revenue is based on actual sales. Enter ticket price and capacity. The system calculates revenue from the percentage of tickets sold.',
    doc_status_workflow: 'Status Workflow',
    doc_tentative: 'Planned',
    doc_confirmed: 'Confirmed',
    doc_completed: 'Completed',
    doc_cancelled: 'Cancelled',
    doc_tentative_desc: 'Initial planning phase, date not final',
    doc_confirmed_desc: 'Date and venue locked, ready for promotion',
    doc_completed_desc: 'Concert finished, financial data finalized',
    doc_cancelled_desc: 'Event did not happen',
    doc_financial_calc: 'Financial Calculations',
    doc_gross_revenue: 'Gross Revenue',
    doc_gross_revenue_desc: 'Total income (fee + ticket sales + merchandise)',
    doc_direct_expenses: 'Direct Expenses',
    doc_direct_expenses_desc: 'Travel, accommodation, piano rental, other costs',
    doc_processing_fee: 'Processing Fee',
    doc_processing_fee_desc: 'Booking manager commission (expense for artists, income for organizers)',
    doc_revenue_shares_desc: 'Collaborator payments (production, venue management, etc.)',
    doc_net_income: 'Net Income',
    doc_net_income_desc: 'Final profit = Gross - Expenses - Revenue Shares',
    
    // Contacts documentation
    doc_contact_management: 'Contact Management',
    doc_contact_roles: 'Contact Roles',
    doc_role_artist: 'Artist',
    doc_role_artist_desc: 'Performer in concerts',
    doc_role_promoter: 'Promoter',
    doc_role_promoter_desc: 'Event promoters and marketing contacts',
    doc_role_bm: 'Booking Manager',
    doc_role_bm_desc: 'Concert organizers and agents',
    doc_role_vm: 'Venue Manager',
    doc_role_vm_desc: 'Venue managers',
    doc_role_other: 'Other',
    doc_role_other_desc: 'Other business contacts',
    doc_follow_up: 'Follow-Up Dates',
    doc_follow_up_intro: 'Set follow-up dates to remind yourself to recontact contacts:',
    doc_follow_up_1: 'Add a next follow-up date when creating/editing a contact',
    doc_follow_up_2: 'The system shows indicators: Overdue (red), Due Soon (yellow), Scheduled (green)',
    doc_follow_up_3: 'Click "Generate Task" to automatically create a reminder',
    doc_follow_up_4: 'After follow-up, update the date for the next contact',
    doc_visibility: 'Visibility and Sharing',
    doc_visibility_intro: 'Contacts are shared based on collaborations:',
    doc_visibility_1: 'You see contacts you created',
    doc_visibility_2: 'Performers see organizers they have worked with',
    doc_visibility_3: 'Organizers see performers they have booked',
    doc_visibility_4: 'This ensures access to relevant business relationships',
    
    // Revenue Shares documentation
    doc_rs_system: 'Revenue Shares System',
    doc_rs_what: 'What are Revenue Shares?',
    doc_rs_intro: 'Revenue Shares distribute concert revenue among collaborators:',
    doc_rs_performance: 'Performance',
    doc_rs_performance_desc: 'Artist share of revenue',
    doc_rs_booking: 'Booking',
    doc_rs_booking_desc: 'Booking manager commission',
    doc_rs_venue: 'Venue Management',
    doc_rs_venue_desc: 'Venue owner share',
    doc_rs_production: 'Production',
    doc_rs_production_desc: 'Sound engineers, technicians, etc.',
    doc_rs_other: 'Other',
    doc_rs_other_desc: 'Other revenue sharing agreements',
    doc_rs_calculation: 'How Calculations Work',
    doc_rs_calc_bases: 'Calculation Bases:',
    doc_rs_gross: 'Gross Revenue',
    doc_rs_gross_desc: 'Total income before deductions',
    doc_rs_net: 'Net Revenue',
    doc_rs_net_desc: 'Income after direct expenses',
    doc_rs_ticket_only: 'Ticket Sales Only',
    doc_rs_ticket_only_desc: 'Only ticket revenue (excludes fee)',
    doc_rs_fee_only: 'Fee Only',
    doc_rs_fee_only_desc: 'Only the fixed fee amount',
    doc_rs_share_types: 'Share Types:',
    doc_rs_percentage: 'Percentage',
    doc_rs_percentage_desc: 'Calculated as % of selected base',
    doc_rs_fixed: 'Fixed Amount',
    doc_rs_fixed_desc: 'Guaranteed fixed payment',
    doc_rs_workflow: 'Revenue Shares Workflow',
    doc_rs_step1: 'Create revenue shares during concert planning',
    doc_rs_step2: 'Status starts as "Pending"',
    doc_rs_step3: 'After the concert, update to "Paid" when payment is made',
    doc_rs_step4: 'Optionally set to "Invoiced" when invoice is issued',
    doc_rs_step5: 'Add payment date and notes for tracking',
    doc_rs_pending: 'Pending',
    doc_rs_invoiced: 'Invoiced',
    doc_rs_paid: 'Paid',
    
    // Analytics documentation
    doc_analytics_reports: 'Analytics and Reports',
    doc_analytics_overview: 'Analytics Overview',
    doc_analytics_intro: 'The Analytics page offers interactive charts on:',
    doc_analytics_1: 'Revenue vs Expenses over time',
    doc_analytics_2: 'Concert distribution by status',
    doc_analytics_3: 'Performance by venue and organizer',
    doc_analytics_4: 'Seasonal trends and year comparison',
    doc_data_export: 'Data Export',
    doc_export_intro: 'You can export data in different formats:',
    doc_export_csv: 'CSV',
    doc_export_csv_desc: 'For import into Excel/Sheets',
    doc_export_pdf: 'PDF',
    doc_export_pdf_desc: 'For professional reporting',
    doc_export_json: 'JSON',
    doc_export_json_desc: 'Complete GDPR export from profile',
    doc_export_tip: 'Use filters for year and status to export only relevant data.',
    
    // Mobile documentation
    doc_mobile_usage: 'Mobile Usage',
    doc_mobile_features: 'Mobile Features',
    doc_mobile_intro: 'The app is fully responsive and optimized for mobile use:',
    doc_mobile_1: 'Sidebar that automatically closes after navigation',
    doc_mobile_2: 'Touch-optimized buttons with minimum 44px target',
    doc_mobile_3: 'Swipe gestures for calendar and lists',
    doc_mobile_4: 'Optimized forms to avoid iOS zoom on input',
    doc_mobile_tips: 'Mobile Tips',
    doc_mobile_tip1: 'Use the calendar in monthly view for a quick overview',
    doc_mobile_tip2: 'Add the app to Home Screen for quick access',
    doc_mobile_tip3: 'Data is synced in real-time between devices',
    
    // Video tutorials
    video_intro: 'Watch video tutorials to quickly learn the main features',
    video_coming_soon: 'Video tutorials are being prepared. Check back regularly for updates!',
    video_1_title: 'First Access: Dashboard and Navigation',
    video_2_title: 'Creating Your First Concert',
    video_3_title: 'Configuring Revenue Shares',
    video_4_title: 'Using the AI Music Assistant',
    video_5_title: 'Analyzing Financial Performance',
    video_6_title: 'Managing Contacts and Follow-ups',
    video_target_all: 'All',
    video_target_bm: 'Booking Manager',
    video_target_artist_bm: 'Artist / Booking Manager',
    video_target_artist_acc: 'Artist / Accountant',
    
    // Role guides
    role_artist: 'Artist',
    role_booking_manager: 'Booking Manager',
    role_venue_manager: 'Venue Manager',
    
    // Artist guide steps
    artist_step1_title: 'Dashboard Overview - Your Statistics at a Glance',
    artist_step1_desc: 'The Dashboard is your personal control center. Here you see confirmed concerts, total net income, upcoming dates, and the most important statistics.',
    artist_step1_tip1: 'Use the top filters to select the year of interest',
    artist_step1_tip2: 'Statistics update automatically when you add new concerts',
    artist_step1_tip3: 'Click on "View Details" to dive deeper into each metric',
    artist_step2_title: 'Viewing Your Concerts',
    artist_step2_desc: 'On the Concerts page you can see all your past and future events. Use filters for status (confirmed, tentative, completed) and year to quickly find what you are looking for.',
    artist_step2_tip1: 'Click on a concert to see all financial details',
    artist_step2_tip2: 'Use the calendar for a monthly view of your commitments',
    artist_step2_tip3: 'Export data to CSV or PDF for your documentation',
    artist_step3_title: 'Checking Earnings - My Fees',
    artist_step3_desc: 'The My Fees section shows your total net income as an artist, divided by year. You also see the breakdown by concert type and top clients (organizers who booked you the most).',
    artist_step3_tip1: 'Net income is calculated automatically: revenue - expenses - commissions',
    artist_step3_tip2: 'Check "Top Organizers" to understand who brings you the most work',
    artist_step3_tip3: 'Use charts to identify seasonal trends in your earnings',
    artist_step4_title: 'Checking Revenue Shares - Who Owes You Money',
    artist_step4_desc: 'On the Revenue Shares page you see all payments you need to receive or make. Each concert can have revenue shares for collaborating artists, booking managers, venue managers, etc.',
    artist_step4_tip1: 'Filter by "Pending" status to see who still owes you payment',
    artist_step4_tip2: 'Update status to "Paid" when you receive payment',
    artist_step4_tip3: 'Add notes to track payment methods and dates',
    artist_step5_title: 'Using the AI Assistant for Quick Questions',
    artist_step5_desc: 'The AI Music Assistant answers questions about your data in natural language. Ask "How much did I earn this year?" or "Show me the next 3 concerts" to get immediate answers.',
    artist_step5_tip1: 'Try the suggested prompts to discover all features',
    artist_step5_tip2: 'You can ask for statistics, concert lists, venue analysis',
    artist_step5_tip3: 'Export conversations to PDF for reporting',
    
    // Booking Manager guide steps
    bm_step1_title: 'Creating a New Concert - Complete Step',
    bm_step1_desc: 'Click "New Concert" from the Concerts page. Fill in all required fields: title, date, artist (performer), organizer, venue. Add financial details like performance fee, ticket price, travel expenses.',
    bm_step1_tip1: 'Make sure to select yourself or your agency as organizer',
    bm_step1_tip2: 'Set the processing fee percentage to automatically calculate commissions',
    bm_step1_tip3: 'Save as "Tentative" if not yet confirmed, then update to "Confirmed"',
    bm_step2_title: 'Managing Artist and Venue Contacts',
    bm_step2_desc: 'On the Contacts page you can create and manage your database of artists, venue managers, technicians. Each contact can have role, email, phone, custom notes.',
    bm_step2_tip1: 'Add "Monthly Retainer Fee" for artists with fixed monthly contracts',
    bm_step2_tip2: 'Use notes to track artist preferences (piano, backstage, etc.)',
    bm_step2_tip3: 'Enable "Auto-generate tasks" for automatic follow-up reminders',
    bm_step3_title: 'Configuring Processing Fee and Commissions',
    bm_step3_desc: 'The processing fee is your commission as a booking manager. You can set it at concert level ("Processing Fee %" field) or at contact level (organizer) to apply it automatically.',
    bm_step3_tip1: 'Commission is calculated on total revenue (ticket sales or performance fee)',
    bm_step3_tip2: 'Set processing fee between 10-20% according to market standards',
    bm_step3_tip3: 'Monitor total commissions in the My Fees section',
    bm_step4_title: 'Tracking Commissions - My Fees BM Section',
    bm_step4_desc: 'The My Fees section shows your total commissions as a booking manager, divided between percentage commissions on concerts and fixed monthly retainer fees from artists.',
    bm_step4_tip1: 'See breakdown by concert type to understand where you earn the most',
    bm_step4_tip2: 'The "Top Artists" section shows which artists generate the most revenue for you',
    bm_step4_tip3: 'Export data for tax reporting or accounting',
    bm_step5_title: 'Reports and Analytics - Data Export',
    bm_step5_desc: 'The Analytics page offers interactive charts on revenue, expenses, commissions by period. You can export data to CSV or PDF to send to clients or accountants.',
    bm_step5_tip1: 'Use year filters to compare performance across periods',
    bm_step5_tip2: 'The "Revenue vs Expenses" chart shows financial trend over time',
    bm_step5_tip3: 'Export the "Completed Concerts" list for contract documentation',
    
    // Venue Manager guide steps
    vm_step1_title: 'Registering a Venue - Details and Capacity',
    vm_step1_desc: 'Go to the Venues page and click "New Venue". Enter name, address (use autocomplete to automatically fill city and country), capacity, piano availability.',
    vm_step1_tip1: 'Specify maximum capacity to correctly calculate expected revenue',
    vm_step1_tip2: 'Indicate piano availability and model to facilitate concert planning',
    vm_step1_tip3: 'Add typical rental costs and ticket commissions for transparency',
    vm_step2_title: 'Viewing Concerts at the Venue',
    vm_step2_desc: 'From the Concerts page, use the "Venue" filter to see all events scheduled at your location. You can also use the Calendar for a monthly view.',
    vm_step2_tip1: 'The calendar shows events with different colors by status',
    vm_step2_tip2: 'Click on an event to see complete details',
    vm_step2_tip3: 'Use filters to hide cancelled or completed events',
    vm_step3_title: 'Managing Piano Availability',
    vm_step3_desc: 'When editing a venue, you can specify if a piano is available, the exact model, and any piano rental costs. This information helps booking managers in planning.',
    vm_step3_tip1: 'Clearly indicate if the piano is included in rental or has a separate cost',
    vm_step3_tip2: 'Specify brand and model for artists with specific requirements',
    vm_step3_tip3: 'Update this info if available equipment changes',
    vm_step4_title: 'Collaborating with Booking Managers',
    vm_step4_desc: 'Booking managers can see your venues when creating concerts. Make sure your contacts (email, phone) are updated in the Profile section to facilitate communication.',
    vm_step4_tip1: 'Respond quickly to requests to confirm date availability',
    vm_step4_tip2: 'Use notes in concerts to communicate technical requirements',
    vm_step4_tip3: 'Keep rental costs updated to avoid surprises',
    
    // FAQs
    faq_title: 'Frequently Asked Questions',
    faq_intro: 'Answers to the most common questions about Maestro Music Manager',
    faq1_q: 'How do I add a new concert?',
    faq1_a: 'Go to the Concerts page, click the "New Concert" button in the top right. Fill in the required fields (title, date, performer, organizer, venue) and save. You can set the status to "Tentative" if not yet confirmed.',
    faq2_q: 'How are booking manager commissions calculated?',
    faq2_a: 'Commissions are calculated by applying the "Processing Fee %" to the total concert revenue (ticket sales or performance fee). You can set this percentage in the "Processing Fee %" field when creating/editing a concert, or save it directly in the organizer contact to apply it automatically.',
    faq3_q: 'Can I export my data?',
    faq3_a: 'Yes! From the Analytics page you can export the completed concerts list in CSV or PDF format. Additionally, in profile settings you will find the "Export Data" option to download all your data in JSON format (GDPR compliance).',
    faq4_q: 'How does the AI Music Assistant work?',
    faq4_a: 'The AI Assistant is a tool that answers questions about your data in natural language. You can ask for statistics ("How much did I earn this year?"), lists ("Show me the next 3 concerts"), analysis ("Which venue is most profitable?") and much more. The AI accesses your authorized data and provides immediate answers.',
    faq5_q: 'What are Revenue Shares?',
    faq5_a: 'Revenue Shares are profit shares to be distributed among the various stakeholders of a concert (collaborating artists, booking managers, venue managers, promoters). Each concert can have multiple revenue shares with percentages or fixed amounts, and you can track their payment status (Pending/Paid).',
    faq6_q: 'Can I manage multiple roles simultaneously?',
    faq6_a: 'Yes! Maestro supports multi-role users. For example, you can be both Artist and Booking Manager. The system automatically shows pages and data relevant to all your active roles. Analytics and Dashboard aggregate data from all your perspectives.',
    faq7_q: 'How do I link a contact to my user account?',
    faq7_a: 'When creating or editing a contact, you can select "User Email" from the dropdown to associate it with an existing account. This is useful when an artist has a Maestro account and you want to link their contact to their login. The association enables collaborative scenarios like booking managers managing registered artists.',
    faq8_q: 'Can I duplicate a concert?',
    faq8_a: 'Yes! On the Concerts page, click the three dots next to a concert and select "Duplicate". The system automatically creates a new concert with all copied data (performer, organizer, venue, fees), date set to tomorrow and "(Copy)" added to the title. You can then quickly edit it to adapt.',
    
    // Fan CRM
    fan_crm_description: 'Manage your fan base and engagement',
    add_fan: 'Add Fan',
    edit_fan: 'Edit Fan',
    total_fans: 'Total Fans',
    vip_fans: 'VIP Fans',
    total_ltv: 'Total LTV',
    email_consent_rate: 'Email Consent',
    search_fans: 'Search fans...',
    filter_tier: 'Filter by tier',
    filter_source: 'Filter by source',
    filter_city: 'Filter by city',
    all_tiers: 'All Tiers',
    all_sources: 'All Sources',
    only_consented: 'Only with consent',
    no_fans_found: 'No fans found',
    no_fans_description: 'Add your first fan to start building your database',
    add_first_fan: 'Add first fan',
    concerts_attended: 'concerts',
    delete_fan_title: 'Delete Fan',
    delete_fan_description: 'Are you sure you want to delete this fan? This action cannot be undone.',
    basic_info: 'Basic Info',
    engagement: 'Engagement',
    email: 'Email',
    first_name: 'First Name',
    last_name: 'Last Name',
    phone: 'Phone',
    birth_date: 'Birth Date',
    city: 'City',
    country: 'Country',
    source: 'Source',
    fan_tier: 'Fan Tier',
    notes: 'Notes',
    notes_placeholder: 'Add notes about this fan...',
    engagement_description: 'These metrics are calculated automatically based on fan activity.',
    lifetime_value: 'Lifetime Value',
    merchandise_spent: 'Merchandise Spent',
    emails_opened: 'Emails Opened',
    emails_clicked: 'Emails Clicked',
    gdpr_info_title: 'GDPR Consent',
    gdpr_info_description: 'Manage consent for email communications and marketing activities in compliance with GDPR regulations.',
    email_consent_label: 'Email Communications',
    email_consent_description: 'The fan has consented to receive email communications.',
    marketing_consent_label: 'Marketing Activities',
    marketing_consent_description: 'The fan has consented to marketing and promotional activities.',
    consent_recorded: 'Consent recorded on',
    // Phase 5: Attendance tracking
    add_attendance: 'Add Attendance',
    edit_attendance: 'Edit Attendance',
    concert: 'Concert',
    select_concert: 'Select a concert',
    ticket_type: 'Ticket Type',
    ticket_price: 'Ticket Price',
    external_ticket_id: 'External Ticket ID',
    checked_in: 'Checked In',
    checked_in_description: 'Mark if the fan has checked in at the venue',
    concert_attendees: 'Concert Attendees',
    total_attendees: 'Total Attendees',
    ticket_revenue: 'Ticket Revenue',
    search_attendees: 'Search attendees...',
    no_attendees_found: 'No attendees found for this concert',
    delete_attendance_title: 'Remove Attendance',
    delete_attendance_description: 'Are you sure you want to remove this attendance record?',
    no_attendance: 'No concert attendance recorded',
    
    // Access denied
    access_denied: 'Access Denied',
    access_denied_description: 'You do not have permission to access this page.',
    access_denied_help: 'If you believe this is an error, please contact an administrator.',
    go_to_dashboard: 'Go to Dashboard',
    go_back: 'Go Back',
    
    // Tasks Page
    tasks_title: 'Tasks',
    tasks_description: 'Manage your activities and deadlines',
    total_tasks: 'Total Tasks',
    all_tasks: 'All tasks',
    past_due_date: 'Past due date',
    currently_active: 'Currently active',
    finished_tasks: 'Finished tasks',
    task_board: 'Task Board',
    todo: 'To Do',
    no_tasks_yet: 'No tasks yet',
    create_first_task: 'Create your first task to start organizing your work',
    add_first_task: 'Add Your First Task',
    delete_task_confirm: 'This action cannot be undone. This will permanently delete the task.',
    assigned: 'Assigned',
    today_label: 'Today',
    overdue_label: 'Overdue',
    
    // Expenses Page
    expenses_title: 'Expenses',
    expenses_description: 'Track and manage all your expenses',
    total_expenses: 'Total Expenses',
    tax_deductible: 'Tax Deductible',
    total_items: 'Total Items',
    search_expenses: 'Search expenses...',
    all_categories: 'All Categories',
    delete_expense: 'Delete Expense',
    delete_expense_confirm: 'Are you sure you want to delete this expense? This action cannot be undone.',
    no_expenses_found: 'No expenses found',
    no_expenses_yet: 'No expenses yet',
    start_tracking: 'Start tracking your expenses',
    add_first_expense: 'Add Your First Expense',
    recurring: 'Recurring',
    tax: 'Tax',
    date_label: 'Date',
    title_label: 'Title',
    category_label: 'Category',
    amount_label: 'Amount',
    status_label: 'Status',
    actions_label: 'Actions',
    
    // MyFees Page
    my_fees_title: 'My Fees',
    my_fees_description: 'Track your earnings and commissions',
    booking_commissions: 'Booking Commissions',
    from_organized_concerts: 'From organized concerts',
    monthly_retainers: 'Monthly Retainers',
    annual_retainers: 'Annual retainers',
    avg_commission: 'Avg Commission',
    per_concert: 'Per concert',
    top_client: 'Top Client',
    monthly_trend: 'Monthly Trend',
    fees_by_concert_type: 'Fees by Concert Type',
    top_clients_by_commission: 'Top 5 Clients by Commission',
    monthly_retainer_contracts: 'Monthly Retainer Contracts',
    performance_fees: 'Performance Fees',
    from_performances: 'From performances',
    avg_fee: 'Avg Fee',
    per_performance: 'Per performance',
    top_organizer: 'Top Organizer',
    monthly_performance_fees: 'Monthly Performance Fees',
    top_organizers: 'Top 5 Organizers',
    total_income: 'Total Income',
    total_income_all_sources: 'Total Income (All Sources)',
    combined_earnings: 'Combined booking commissions + performance fees + retainers',
    month_label: 'month',
    annual: 'Annual',
    
    // Profile Page
    profile_title: 'Profile',
    profile_description: 'Manage your account preferences',
    account_information: 'Account Information',
    email_and_roles: 'Your email and current roles',
    current_roles: 'Current Roles',
    no_roles_assigned: 'No roles assigned',
    personal_information: 'Personal Information',
    update_personal_details: 'Update your personal details',
    full_name: 'Full Name',
    your_full_name: 'Your full name',
    your_company_name: 'Your company name',
    preferences: 'Preferences',
    customize_settings: 'Customize your application settings',
    language_label: 'Language',
    timezone_label: 'Timezone',
    currency_label: 'Currency',
    save_personal_info: 'Save Personal Info',
    onboarding_section: 'Onboarding',
    restart_checklist: 'Restart the getting started checklist',
    reset_onboarding: 'Reset Onboarding Checklist',
    resetting: 'Resetting...',
    no_contact_associated: 'No contact associated. Ask an admin to link your account to a contact.',
    profile_updated: 'Profile updated successfully',
    profile_update_error: 'Failed to update profile',
    personal_info_updated: 'Personal information updated successfully',
    personal_info_error: 'Failed to update personal information',
    onboarding_reset_success: 'Onboarding checklist has been reset and will appear on the dashboard',
    onboarding_reset_error: 'Failed to reset onboarding checklist',
    
    // Settings Page
    settings_title: 'Settings',
    settings_description: 'Manage your account and privacy preferences',
    privacy_data_management: 'Privacy & Data Management',
    privacy_policy: 'Privacy Policy',
    terms_of_service: 'Terms of Service',
    app_update_management: 'App Update Management',
    send_update_to_all: 'Send update to all users',
    send_update_description: 'Force all connected users to reload the app with the latest version.',
    update_sent_success: 'Update notification sent to all connected users',
    update_sent_error: 'Failed to send update notification',
    sending_update: 'Sending...',
    contact_privacy_team: 'Contact Privacy Team',
    upgrade_success: 'Upgrade completed successfully! Welcome to your new plan.',
    
    // RevenueShares Page
    revenue_shares_title: 'Revenue Shares',
    revenue_shares_description: 'Manage revenue distribution among collaborators and artists',
    total_revenue_shares: 'Total Revenue Shares',
    all_collaborations: 'All collaborations',
    pending_payments: 'Pending Payments',
    to_calculate_pay: 'To calculate/pay',
    paid_shares: 'Paid Shares',
    total_to_pay: 'Total to Pay',
    calculated_shares: 'Calculated shares',
    to_configure: 'To Configure',
    concerts_without_shares: 'Concerts without shares',
    filters: 'Filters',
    search_concert_collaborator: 'Search by concert or collaborator...',
    filter_by_status: 'Filter by status',
    all_statuses: 'All statuses',
    pending: 'Pending',
    calculated: 'Calculated',
    paid: 'Paid',
    disputed: 'Disputed',
    filter_by_type: 'Filter by type',
    all_types_shares: 'All types',
    revenue_distribution: 'Revenue Distribution',
    beneficiary: 'Beneficiary',
    share_type: 'Type',
    percentage: 'Percentage',
    fixed_amount: 'Fixed Amount',
    calculation_base: 'Calculation Base',
    calculated_amount: 'Amount',
    calculate: 'Calculate',
    mark_as_paid: 'Mark as Paid',
    bulk_actions: 'Bulk Actions',
    selected: 'selected',
    calculate_selected: 'Calculate Selected',
    mark_selected_paid: 'Mark as Paid',
    deselect_all: 'Deselect',
    no_revenue_shares: 'No revenue shares yet',
    configure_first_share: 'Revenue shares will be created automatically from concerts',
    configure: 'Configure',
    internal_user: 'Internal user',
    unknown: 'Unknown',
    concerts_without_collaborators: 'Concerts Without Collaborators',
    export_completed: 'Export completed',
    collaborations_exported: 'Revenue shares exported successfully',
    calculation_completed: 'Calculation completed - Amount calculated successfully',
    collaborations_marked_paid: 'collaborations marked as paid',
    collaborations_calculated: 'collaborations calculated',
    no_collaboration_found: 'No collaboration found',
    modify_filters: 'Try adjusting your filters',
    
    // Calendar Page (additional keys)
    artistic_projects_label: 'Artistic Projects',
    concert_rescheduled: 'Concert rescheduled',
    failed_reschedule: 'Failed to reschedule concert',
    concert_deleted_success: 'Concert deleted successfully',
    no_permission_delete: 'You do not have permission to delete this concert',
    
    // Analytics Page
    analytics_title: 'Analytics',
    analytics_description: 'Detailed performance and revenue analysis',
    failed_load_analytics: 'Failed to load analytics. Please try again.',
    no_concerts_for_filters: 'No concerts found for selected filters',
    gross_volume: 'Gross Volume',
    total_revenue_label: 'Total revenue',
    direct_expenses_label: 'Direct Expenses',
    total_expenses_label: 'Total expenses',
    revenue_shares_kpi: 'Revenue Shares',
    collaborators_label: 'Collaborators',
    final_earnings: 'Final earnings',
    total_commissions: 'Total Commissions',
    booking_manager_commissions: 'Booking manager commissions',
    revenue_breakdown: 'Revenue Breakdown',
    expense_breakdown: 'Expense Breakdown',
    loading_profile: 'Loading profile...',
    access_denied_roles: 'You do not have the required permissions to view this section. Required roles: Admin, Artist, Booking Manager, Accountant.',
    custom_view_description: 'You are viewing concerts where you are',
    multi_role_description: 'multi-role: viewing all concerts that concern you',
    viewing_organizer_only: 'You are viewing only concerts where you are the organizer.',
    viewing_performer_only: 'You are viewing only concerts where you are the performer.',
    viewing_vm_only: 'You are viewing only concerts in your venues.',
    viewing_relevant: 'You are viewing only concerts that concern you.',
    all_filter: 'All',
    completed_filter: 'Completed',
    confirmed_filter: 'Confirmed',
    export_pdf: 'Export PDF',
    net_income_trend: 'Net Income Trend',
    net_income_label: 'Net Income',
    revenue_share_analysis: 'Revenue Share Analysis',
    revenue_shares_by_type: 'Revenue Shares by Type',
    top_share_recipients: 'Top Share Recipients',
    concert_analysis: 'Concert Analysis',
    concert_status_distribution: 'Concert Status Distribution',
    profitability_by_type: 'Profitability by Type',
    monthly_audience_growth: 'Monthly Audience Growth',
    concerts_by_venue_type: 'Concerts by Venue Type',
    top_venues_by_revenue: 'Top Venues by Revenue',
    top_organizers_by_commission: 'Top Organizers by Commission',
    top_artists_by_revenue: 'Top Artists by Revenue',
    top_booking_managers_by_commission: 'Top Booking Managers by Commission',
    concerts_by_country: 'Concerts by Country',
    concerts_by_region: 'Concerts by Region (Italy)',
    artistic_project_performance: 'Performance by Artistic Project',
    revenue_by_project: 'Revenue by Project',
    concerts_by_project: 'Concerts by Project',
    artistic_project_details: 'Artistic Project Details',
    project_label: 'Project',
    total_concerts_label: 'Concerts',
    total_audience_label: 'Total Audience',
    avg_revenue_per_concert: 'Avg Revenue/Concert',
    avg_audience_per_concert: 'Avg Audience/Concert',
    concert_list: 'Concert List',
    completed_concerts: 'Completed Concerts',
    revenue_table_label: 'Revenue',
    commission_table_label: 'Commission',
    cachet_label: 'Performance Fee',
    tickets_label: 'Tickets',
    merchandise_label: 'Merchandise',
    travel_label: 'Travel',
    accommodation_label: 'Accommodation',
    piano_rental_label: 'Piano Rental',
    other_label: 'Other',
    commissions_label: 'Processing Fees',
    verifying_credentials: 'Verifying credentials...',
    loading_data: 'Loading data...',
    
    // Purchase Intelligence
    purchase_intelligence: 'Purchase Intelligence',
    total_revenue: 'Total Revenue',
    avg_order_value: 'Avg Order Value',
    gap_revenue: 'Gap Revenue',
    conversion_rate: 'Conversion Rate',
    missing_product: 'Missing Product',
    all_products: 'All Products',
    tier: 'Tier',
    purchased: 'Products',
    missing: 'Missing',
    missing_products: 'Missing Products',
    orders: 'Orders',
    purchased_products: 'Purchased Products',
    have_not_purchased: 'have not purchased',
    create_segment: 'Create Segment',
    add_offline_purchase: 'Add Offline Purchase',
    register_offline_purchase: 'Register Offline Purchase',
    register_offline_purchase_description: 'Record an offline purchase to keep complete tracking.',
    product: 'Product',
    quantity: 'Quantity',
    unit_price: 'Unit Price',
    purchase_date: 'Purchase Date',
    save_purchase: 'Save Purchase',
    add_purchase: 'Add Purchase',
    view_details: 'View Details',
    product_matrix: 'Product Matrix',
    no_products_available: 'No products available',
    no_results: 'No results found',

    // Tasks page (extended)
    tasks_page_description: 'Manage team and personal tasks',
    new_task: 'New task',
    no_tasks_short: 'No tasks',
    create_first_task_short: 'Create your first task to get started',
    create_task_short: 'Create task',
    stat_total: 'Total',
    stat_overdue: 'Overdue',
    stat_in_progress: 'In progress',
    stat_completed: 'Completed',
    board_todo: 'To do',
    board_in_progress: 'In progress',
    board_completed: 'Completed',
    board_cancelled: 'Cancelled',
    delete_task: 'Delete task',
    delete_task_description: "Are you sure you want to delete this task? This action cannot be undone.",
    edit_task: 'Edit task',
    form_title: 'Title',
    form_description: 'Description',
    form_category: 'Category',
    form_status: 'Status',
    form_priority: 'Priority',
    form_assigned_to: 'Assigned to',
    form_linked_contact: 'Linked contact',
    form_due_date: 'Due date',
    form_reminder: 'Reminder',
    reminder_hint: 'When to remind you of this task',
    pick_date: 'Pick a date',
    select_category_placeholder: 'Select a category',
    select_user_placeholder: 'Select user',
    unassigned_label: 'Unassigned',
    select_contact_placeholder: 'Select contact',
    no_contact_option: 'No contact',
    update_task: 'Update task',
    create_task_action: 'Create task',
    category_followup: 'Follow-up',
    category_contract: 'Contract',
    category_payment: 'Payment',
    category_planning: 'Planning',
    category_other: 'Other',
    priority_low: 'Low',
    priority_medium: 'Medium',
    priority_high: 'High',
    priority_urgent: 'Urgent',
    status_todo: 'To do',
    status_in_progress: 'In progress',
    status_completed: 'Completed',
    status_cancelled: 'Cancelled',
    task_actions: 'Task actions',
    move_to: 'Move to',
    created_by_label: 'Created by',
    csv_col_title: 'Title',
    csv_col_description: 'Description',
    csv_col_category: 'Category',
    csv_col_status: 'Status',
    csv_col_priority: 'Priority',
    csv_col_due_date: 'Due date',
    csv_col_assignee: 'Assignee',
    mark_completed: 'Mark as completed',
    mark_todo: 'Mark as to do',
  },
  it: {
    // Navigation
    dashboard: 'Dashboard',
    calendar: 'Calendario',
    concerts: 'Concerti',
    artistic_projects: 'Progetti Artistici',
    annual_plan: 'Piano Annuale',
    music_catalog: 'Catalogo Musicale',
    repertoire: 'Repertorio',
    contacts: 'Contatti',
    venues: 'Luoghi',
    contracts: 'Contratti',
    templates: 'Modelli Contratto',
    tasks: 'Attività',
    my_fees: 'Le Mie Commissioni',
    revenue_shares: 'Collaborazioni',
    expenses: 'Spese',
    media_library: 'Libreria Media',
    cost_calculator: 'Calcolatore Costi',
    tour_calculator: 'Tour Calculator',
    analytics: 'Analisi',
    ai_expert: 'Esperto AI Musicale',
    tutorials: 'Tutorial',
    user_management: 'Gestione Utenti',
    settings: 'Impostazioni',
    profile: 'Profilo',
    logout: 'Esci',
    fan_crm: 'Fan CRM',
    fan_analytics: 'Analisi Fan',
    products: 'Prodotti',
    integrations: 'Integrazioni',
    help: 'Aiuto',
    help_center: 'Centro Aiuto',
    
    // Menu sections
    section_core: 'Principale',
    section_management: 'Gestione',
    section_fan_marketing: 'Fan & Marketing',
    section_financial: 'Finanziario',
    section_tools: 'Strumenti',
    section_account: 'Account',
    section_admin: 'Amministrazione',
    payment_analytics: 'Analisi Pagamenti',
    cms_admin: 'Gestione CMS',
    blog_admin: 'Gestione Blog',
    platform_updates_admin: 'Novità Piattaforma',
    auto_comms_admin: 'Comunicazioni Auto',
    landing_images_admin: 'Immagini Landing',
    landing_settings_admin: 'Impostazioni Landing',
    
    // Admin users management
    admin_users_title: 'Utenti Registrati',
    admin_users_description: 'Gestisci tutti gli utenti della piattaforma',
    search_users: 'Cerca per email o organizzazione...',
    filter_by_plan: 'Filtra per piano',
    all_plans: 'Tutti i piani',
    no_plan: 'Nessun piano',
    no_organization: 'Nessuna organizzazione',
    no_users_found: 'Nessun utente trovato',
    no_users_registered: 'Nessun utente registrato',
    total_users: 'Totale utenti',
    showing_filtered: 'visualizzati',
    registered_at: 'Registrato',
    confirm_delete_user: 'Elimina Utente',
    delete_user_warning: 'Sei sicuro di voler eliminare l\'utente',
    delete_user_permanent: 'Questa azione è irreversibile. Tutti i dati dell\'utente saranno eliminati permanentemente.',
    
    // Venues
    total_venues: 'Totale Luoghi',
    with_piano: 'Con Pianoforte',
    avg_capacity: 'Capacità Media',
    managed_locations: 'Location gestite',
    piano_available: 'Pianoforte disponibile',
    average_seats: 'Posti medi',
    all_venues: 'Tutti i Luoghi',
    no_venues_yet: 'Nessun luogo ancora',
    add_first_venue_desc: 'Aggiungi il tuo primo luogo per iniziare a organizzare concerti',
    add_first_venue: 'Aggiungi il Tuo Primo Luogo',
    add_venue: 'Aggiungi Luogo',
    delete_venue: 'Elimina Luogo',
    delete_venue_confirm: 'Sei sicuro di voler eliminare questo luogo? Questa azione non può essere annullata.',
    venue_deleted: 'Luogo eliminato con successo',
    venue_delete_error: 'Errore nell\'eliminazione del luogo',
    capacity_label: 'Capienza',
    internal_manager: 'Manager Interno',
    venue_contact: 'Contatto Venue',
    commission_label: 'Commissione',
    venue_name: 'Nome Luogo',
    venue_type: 'Tipo Luogo',
    select_venue_type: 'Seleziona tipo luogo',
    select_manager: 'Seleziona manager',
    select_venue_contact: 'Seleziona contatto venue',
    ticket_commission: 'Commissione Biglietti %',
    typical_rental_cost: 'Costo Rental Tipico (€)',
    piano_availability: 'Disponibilità Piano',
    select_piano: 'Seleziona disponibilità piano',
    piano_none: 'Nessuno',
    piano_acoustic: 'Acustico',
    piano_digital: 'Digitale',
    venue_created: 'Luogo creato con successo',
    venue_updated: 'Luogo aggiornato con successo',
    venue_create_error: 'Errore nella creazione del luogo',
    venue_update_error: 'Errore nell\'aggiornamento del luogo',
    add_new_venue: 'Aggiungi Nuovo Luogo',
    edit_venue: 'Modifica Luogo',
    update_venue: 'Aggiorna Luogo',
    create_venue: 'Crea Luogo',
    filter_by_region: 'Filtra per regione',
    all_regions: 'Tutte le Regioni',
    all_countries: 'Tutte le Nazioni',
    additional_notes: 'Note aggiuntive sul luogo',
    search_address: 'Cerca Indirizzo (Auto-compilazione)',
    search_address_hint: 'Cerca un indirizzo per compilare automaticamente i campi sottostanti',
    address_filled: 'Indirizzo compilato automaticamente',
    showing_venues: 'Mostrando',
    of_venues: 'su',
    venues_label: 'luoghi',
    follow_up_label: 'Follow-up',
    
    // Common
    save: 'Salva',
    saving: 'Salvataggio...',
    cancel: 'Annulla',
    delete: 'Elimina',
    edit: 'Modifica',
    add: 'Aggiungi',
    search: 'Cerca',
    filter: 'Filtra',
    export: 'Esporta',
    loading: 'Caricamento...',
    noData: 'Nessun dato disponibile',
    
    // Dashboard
    welcomeBack: 'Bentornato',
    totalRevenue: 'Ricavi Totali',
    totalExpenses: 'Spese Totali',
    upcomingConcerts: 'Concerti Prossimi',
    pendingTasks: 'Attività in Sospeso',
    dashboard_overview: 'Ecco la panoramica della tua attività musicale per il',
    unlimited_access: 'Accesso illimitato',
    custom_view: 'Vista Personalizzata',
    multi_role_view: 'Vista Multi-Ruolo',
    no_completed_concerts: 'Nessun concerto completato nel',
    as_booking_manager: 'Come Booking Manager',
    as_performer: 'Come Performer',
    revenue: 'Ricavi',
    expenses_label: 'Spese',
    booking_fees: 'Commissioni Booking',
    revenue_shares_label: 'Quote Collaboratori',
    net_income: 'Reddito Netto',
    concerts_organized: 'concerti organizzati',
    concerts_performed: 'concerti eseguiti',
    operating_costs: 'Costi operativi',
    performance_costs: 'Costi performance',
    commission_income: 'Commissioni + retainer mensili',
    managed_for_artists: 'Gestite per artisti',
    after_expenses: 'Al netto delle spese',
    combined_total: 'Totale Combinato',
    all_costs: 'Tutti i costi',
    paid_to_collaborators: 'Pagato ai collaboratori',
    ytd_spending: 'Spese da inizio anno',
    total_after_expenses: 'Totale dopo tutte le spese',
    open_tasks: 'Attività Aperte',
    view_all: 'Vedi Tutte',
    no_open_tasks: 'Nessuna attività aperta - tutto in ordine!',
    in_progress: 'In Corso',
    no_upcoming_concerts: 'Nessun concerto confermato in programma',
    this_month: 'Questo Mese',
    projected_revenue: 'Ricavi Previsti',
    urgent_tasks: 'Attività Urgenti',
    quick_actions: 'Azioni Rapide',
    add_concert: 'Aggiungi Concerto',
    add_expense: 'Aggiungi Spesa',
    add_task: 'Aggiungi Attività',
    import_data: 'Importa Dati',
    organizer_label: 'Organizzatore',
    performer_label: 'Performer',
    est_cost: 'Costo Stim.',
    expected_revenue: 'Ricavo Atteso',
    my_booking_fees: 'Le Mie Commissioni',
    commission_retainers: 'Commissioni + retainer',
    net_income_for_artists: 'Reddito netto per artisti',
    after_all_expenses: 'Dopo tutte le spese',
    of_concerts: 'di',
    concerts_label: 'concerti',
    role_breakdown_toggle: 'Vedi dettaglio per ruolo',
    role_breakdown_hide: 'Nascondi dettaglio per ruolo',
    completed_concerts_short: 'Concerti completati',
    setup_guide: 'Guida iniziale',
    
    // Concerts Page
    concerts_title: 'Concerti',
    concerts_description: 'Gestisci il calendario e i dettagli dei tuoi concerti',
    search_concerts: 'Cerca concerti...',
    all_years: 'Tutti gli Anni',
    all_types: 'Tutti i Tipi',
    all_status: 'Tutti gli Stati',
    fixed_price: 'Cachet Fisso',
    ticket_sales: 'Vendita Biglietti',
    planned: 'Pianificato',
    tentative: 'Pianificato', // legacy alias
    confirmed: 'Confermato',
    completed: 'Completato',
    cancelled: 'Annullato',
    total_concerts: 'Concerti Totali',
    showing: 'Visualizzati',
    no_concerts_yet: 'Nessun concerto ancora',
    add_first_concert_desc: 'Aggiungi il tuo primo concerto per iniziare',
    add_first_concert: 'Aggiungi il Tuo Primo Concerto',
    
    // Calendar page
    calendar_title: 'Calendario Concerti',
    calendar_description: 'Visualizza e gestisci i tuoi concerti ed eventi',
    month: 'Mese',
    week: 'Settimana',
    day: 'Giorno',
    agenda: 'Agenda',
    today: 'Oggi',
    day_details: 'Dettagli Giorno',
    all_projects: 'Tutti i Progetti',
    no_concerts_this_day: 'Nessun concerto in questo giorno',
    select_day_to_view_details: 'Seleziona un giorno per vedere i dettagli',
    day_total: 'Totale giorno',
    not_available: 'Non Disponibile',
    moved_to: 'spostato al',
    concert_confirmed: 'Concerto confermato',
    concert_cancelled: 'Concerto annullato',
    concert_saved: 'Concerto salvato',
    events: 'eventi',
    my_booking_fee: 'La Mia Commissione',
    venue_commission: 'Commissione Venue',
    revenues: 'Ricavi',
    direct_expenses: 'Spese Dirette',
    ticket_sales_label: 'Vendita Biglietti',
    performance_fee: 'Cachet',
    merchandise: 'Merchandising',
    travel: 'Viaggio',
    accommodation: 'Alloggio',
    piano_rental: 'Noleggio Piano',
    booking_commission: 'Commissione Booking',
    other: 'Altro',
    collaborators: 'Collaboratori',
    manage_collaborators: 'Gestisci Collaboratori',
    duplicate: 'Duplica',
    delete_concert: 'Elimina Concerto',
    delete_concert_confirm: 'Sei sicuro di voler eliminare questo concerto? Questa azione non può essere annullata.',
    estimated_costs: 'Costi Stimati',
    total_expected: 'Totale Previsto',
    expected_label: 'Atteso',
    net_income_after_fee: 'Reddito Netto (dopo la mia commissione)',
    my_net_income: 'Il Mio Reddito Netto',
    concert_deleted: 'Concerto eliminato con successo',
    concert_delete_permission: 'Non hai i permessi per eliminare questo concerto.',
    concert_duplicated: 'Concerto duplicato con successo!',
    edit_now: 'Modifica Ora',
    failed_duplicate: 'Errore nella duplicazione del concerto',
    refresh: 'Aggiorna',
    refreshing: 'Aggiornamento concerti in corso...',
    export_csv: 'Esporta CSV',
    no_concerts_export: 'Nessun concerto da esportare',
    concerts_exported: 'Concerti esportati con successo',
    failed_load_concerts: 'Errore nel caricamento concerti. Riprova.',
    
    // Contacts Page
    contacts_title: 'Contatti',
    contacts_description: 'Gestisci la tua rete professionale',
    add_contact: 'Aggiungi Contatto',
    search_contacts: 'Cerca contatti...',
    all_roles: 'Tutti i Ruoli',
    all_followups: 'Tutti i Follow-up',
    contact_role_artist: 'Artista',
    contact_role_promoter: 'Promoter',
    contact_role_venue_manager: 'Gestore Venue',
    contact_role_agent: 'Agente',
    contact_role_other: 'Altro',
    upcoming: 'In Arrivo',
    overdue: 'Scaduto',
    no_followup_set: 'Nessun Follow-up Impostato',
    show_inactive: 'Mostra contatti inattivi',
    no_contacts_found: 'Nessun contatto trovato',
    adjust_filters: 'Prova a modificare i filtri',
    add_first_contact: 'Aggiungi il tuo primo contatto per iniziare',
    contact_directory: 'Rubrica Contatti',
    contacts_tab_team: 'Team',
    contacts_tab_directory: 'Rubrica',
    contacts_team_member: 'Membro del team',
    contacts_team_description: 'Membri della tua organizzazione che collaborano sulla piattaforma',
    contacts_directory_description: 'Contatti esterni: promoter, venue, agenti e altro',
    active: 'Attivo',
    inactive: 'Inattivo',
    due_soon: 'In scadenza',
    generate_followup_task: 'Genera Attività Follow-up',
    deactivate_title: 'Sei sicuro?',
    deactivate_description: 'Questo disattiverà il contatto',
    deactivate_note: 'Non apparirà più nella vista predefinita ma potrà essere riattivato in seguito',
    deactivate: 'Disattiva',
    contact_deactivated: 'Contatto disattivato con successo',
    fee_label: 'commissione',
    no_data_export: 'Nessun dato da esportare',
    warning_dependencies: 'Attenzione: Questo contatto ha dipendenze',
    
    // Help Center
    help_description: 'Guide step-by-step, documentazione e risorse per sfruttare al massimo Maestro Music Manager',
    quick_start: 'Inizia Subito',
    documentation: 'Documentazione',
    video_tutorials: 'Video Tutorial',
    role_guides: 'Guide per Ruolo',
    faq: 'Domande Frequenti',
    start_here: 'Inizia qui',
    start_here_description: 'Scegli il percorso più adatto al tuo ruolo per iniziare velocemente',
    for_artists: 'Per Artisti',
    for_artists_description: 'Traccia i tuoi concerti, monitora guadagni e gestisci revenue shares',
    start_artist_guide: 'Inizia Guida Artista',
    for_booking_managers: 'Per Booking Manager',
    for_booking_managers_description: 'Organizza eventi, calcola commissioni e gestisci artisti',
    start_bm_guide: 'Inizia Guida Booking Manager',
    for_venue_managers: 'Per Venue Manager',
    for_venue_managers_description: 'Registra venue, gestisci disponibilità e collabora con organizzatori',
    start_vm_guide: 'Inizia Guida Venue Manager',
    ai_assistant: 'AI Assistant',
    ai_assistant_description: 'Fai domande sui tuoi dati e ottieni risposte immediate',
    discover_ai: 'Scopri AI Assistant',
    
    // Documentation tabs
    doc_description: 'Documentazione dettagliata su tutte le funzionalità di Maestro Music Manager',
    doc_concerts: 'Concerti',
    doc_contacts: 'Contatti',
    doc_revenue_shares: 'Revenue Shares',
    doc_analytics: 'Analytics',
    doc_mobile: 'Mobile',
    
    // Concerts documentation
    doc_concert_management: 'Gestione Concerti',
    doc_concert_types: 'Tipi di Concerto',
    doc_fixed_fee: 'Cachet Fisso (Fixed Fee)',
    doc_fixed_fee_desc: "L'artista riceve un compenso fisso garantito indipendentemente dalle vendite biglietti. Ideale per venue con budget prevedibili.",
    doc_ticket_sales: 'Vendita Biglietti (Ticket Sales)',
    doc_ticket_sales_desc: 'I ricavi si basano sulle vendite effettive. Inserisci prezzo biglietto e capienza. Il sistema calcola i ricavi dalla percentuale di biglietti venduti.',
    doc_status_workflow: 'Workflow degli Status',
    doc_tentative: 'Pianificato',
    doc_confirmed: 'Confermato',
    doc_completed: 'Completato',
    doc_cancelled: 'Cancellato',
    doc_tentative_desc: 'Fase di pianificazione, data non definitiva',
    doc_confirmed_desc: 'Data e venue bloccati, pronto per promozione',
    doc_completed_desc: 'Concerto terminato, dati finanziari finalizzati',
    doc_cancelled_desc: 'Evento non avvenuto',
    doc_financial_calc: 'Calcoli Finanziari',
    doc_gross_revenue: 'Ricavo Lordo',
    doc_gross_revenue_desc: 'Entrate totali (cachet + vendita biglietti + merchandise)',
    doc_direct_expenses: 'Spese Dirette',
    doc_direct_expenses_desc: 'Viaggio, alloggio, noleggio piano, altri costi',
    doc_processing_fee: 'Processing Fee',
    doc_processing_fee_desc: 'Commissione booking manager (spesa per artisti, entrata per organizzatori)',
    doc_revenue_shares_desc: 'Pagamenti collaboratori (produzione, venue management, ecc.)',
    doc_net_income: 'Reddito Netto',
    doc_net_income_desc: 'Profitto finale = Lordo - Spese - Revenue Shares',
    
    // Contacts documentation
    doc_contact_management: 'Gestione Contatti',
    doc_contact_roles: 'Ruoli dei Contatti',
    doc_role_artist: 'Artista',
    doc_role_artist_desc: 'Performer nei concerti',
    doc_role_promoter: 'Promoter',
    doc_role_promoter_desc: 'Promotori eventi e contatti marketing',
    doc_role_bm: 'Booking Manager',
    doc_role_bm_desc: 'Organizzatori concerti e agenti',
    doc_role_vm: 'Venue Manager',
    doc_role_vm_desc: 'Gestori delle venue',
    doc_role_other: 'Altro',
    doc_role_other_desc: 'Altri contatti business',
    doc_follow_up: 'Date di Follow-Up',
    doc_follow_up_intro: 'Imposta date di follow-up per ricordarti di ricontattare i contatti:',
    doc_follow_up_1: 'Aggiungi una data di prossimo follow-up quando crei/modifichi un contatto',
    doc_follow_up_2: 'Il sistema mostra indicatori: Scaduto (rosso), In Scadenza (giallo), Programmato (verde)',
    doc_follow_up_3: 'Clicca "Genera Task" per creare automaticamente un promemoria',
    doc_follow_up_4: 'Dopo il follow-up, aggiorna la data per il prossimo contatto',
    doc_visibility: 'Visibilità e Condivisione',
    doc_visibility_intro: 'I contatti sono condivisi in base alle collaborazioni:',
    doc_visibility_1: 'Vedi i contatti che hai creato',
    doc_visibility_2: 'I performer vedono gli organizzatori con cui hanno lavorato',
    doc_visibility_3: 'Gli organizzatori vedono i performer che hanno prenotato',
    doc_visibility_4: 'Questo garantisce accesso alle relazioni business rilevanti',
    
    // Revenue Shares documentation
    doc_rs_system: 'Sistema Revenue Shares',
    doc_rs_what: 'Cosa sono le Revenue Shares?',
    doc_rs_intro: 'Le Revenue Shares distribuiscono i ricavi del concerto tra i collaboratori:',
    doc_rs_performance: 'Performance',
    doc_rs_performance_desc: "Quota dell'artista sui ricavi",
    doc_rs_booking: 'Booking',
    doc_rs_booking_desc: 'Commissione del booking manager',
    doc_rs_venue: 'Venue Management',
    doc_rs_venue_desc: 'Quota del proprietario venue',
    doc_rs_production: 'Produzione',
    doc_rs_production_desc: 'Fonici, tecnici, ecc.',
    doc_rs_other: 'Altro',
    doc_rs_other_desc: 'Altri accordi di revenue sharing',
    doc_rs_calculation: 'Come Funzionano i Calcoli',
    doc_rs_calc_bases: 'Basi di Calcolo:',
    doc_rs_gross: 'Ricavo Lordo',
    doc_rs_gross_desc: 'Entrate totali prima delle deduzioni',
    doc_rs_net: 'Ricavo Netto',
    doc_rs_net_desc: 'Entrate dopo spese dirette',
    doc_rs_ticket_only: 'Solo Vendita Biglietti',
    doc_rs_ticket_only_desc: 'Solo ricavi biglietti (esclude cachet)',
    doc_rs_fee_only: 'Solo Cachet',
    doc_rs_fee_only_desc: "Solo l'importo fisso del cachet",
    doc_rs_share_types: 'Tipi di Quota:',
    doc_rs_percentage: 'Percentuale',
    doc_rs_percentage_desc: 'Calcola come % della base selezionata',
    doc_rs_fixed: 'Importo Fisso',
    doc_rs_fixed_desc: 'Pagamento fisso garantito',
    doc_rs_workflow: 'Workflow Revenue Shares',
    doc_rs_step1: 'Crea le revenue shares durante la pianificazione del concerto',
    doc_rs_step2: 'Lo status inizia come "Pending" (in attesa)',
    doc_rs_step3: 'Dopo il concerto, aggiorna a "Paid" quando viene effettuato il pagamento',
    doc_rs_step4: 'Opzionalmente imposta a "Invoiced" quando viene emessa fattura',
    doc_rs_step5: 'Aggiungi data pagamento e note per tracciabilità',
    doc_rs_pending: 'Pending',
    doc_rs_invoiced: 'Invoiced',
    doc_rs_paid: 'Paid',
    
    // Analytics documentation
    doc_analytics_reports: 'Analytics e Report',
    doc_analytics_overview: 'Panoramica Analytics',
    doc_analytics_intro: 'La pagina Analytics offre grafici interattivi su:',
    doc_analytics_1: 'Ricavi vs Spese nel tempo',
    doc_analytics_2: 'Distribuzione concerti per status',
    doc_analytics_3: 'Performance per venue e organizzatore',
    doc_analytics_4: 'Trend stagionali e comparazione anni',
    doc_data_export: 'Esportazione Dati',
    doc_export_intro: 'Puoi esportare i dati in diversi formati:',
    doc_export_csv: 'CSV',
    doc_export_csv_desc: 'Per importazione in Excel/Sheets',
    doc_export_pdf: 'PDF',
    doc_export_pdf_desc: 'Per reportistica professionale',
    doc_export_json: 'JSON',
    doc_export_json_desc: 'Export completo GDPR dal profilo',
    doc_export_tip: 'Usa i filtri per anno e status per esportare solo i dati rilevanti.',
    
    // Mobile documentation
    doc_mobile_usage: 'Uso su Mobile',
    doc_mobile_features: 'Funzionalità Mobile',
    doc_mobile_intro: "L'app è completamente responsive e ottimizzata per uso mobile:",
    doc_mobile_1: 'Sidebar che si chiude automaticamente dopo la navigazione',
    doc_mobile_2: 'Pulsanti ottimizzati per touch con target minimo 44px',
    doc_mobile_3: 'Gesture swipe per calendario e liste',
    doc_mobile_4: 'Form ottimizzati per evitare zoom iOS sull\'input',
    doc_mobile_tips: 'Suggerimenti Mobile',
    doc_mobile_tip1: 'Usa il calendario in vista mensile per una panoramica veloce',
    doc_mobile_tip2: "Aggiungi l'app alla Home Screen per accesso rapido",
    doc_mobile_tip3: 'I dati sono sincronizzati in tempo reale tra dispositivi',
    
    // Video tutorials
    video_intro: 'Guarda i video tutorial per imparare velocemente le funzionalità principali',
    video_coming_soon: 'I video tutorial sono in fase di preparazione. Controlla regolarmente per gli aggiornamenti!',
    video_1_title: 'Primo Accesso: Dashboard e Navigazione',
    video_2_title: 'Creare il Primo Concerto',
    video_3_title: 'Configurare Revenue Shares',
    video_4_title: "Usare l'AI Music Assistant",
    video_5_title: 'Analizzare le Performance Finanziarie',
    video_6_title: 'Gestire Contatti e Follow-up',
    video_target_all: 'Tutti',
    video_target_bm: 'Booking Manager',
    video_target_artist_bm: 'Artist / Booking Manager',
    video_target_artist_acc: 'Artist / Accountant',
    
    // Role guides
    role_artist: 'Artista',
    role_booking_manager: 'Booking Manager',
    role_venue_manager: 'Venue Manager',
    
    // Artist guide steps
    artist_step1_title: 'Dashboard Overview - Panoramica delle tue statistiche',
    artist_step1_desc: 'Il Dashboard è il tuo centro di controllo personale. Qui vedi i concerti confermati, il reddito netto totale, le prossime date e le statistiche più importanti.',
    artist_step1_tip1: "Usa i filtri in alto per selezionare l'anno di interesse",
    artist_step1_tip2: 'Le statistiche si aggiornano automaticamente quando aggiungi nuovi concerti',
    artist_step1_tip3: 'Clicca su "Vista Dettagli" per approfondire ogni metrica',
    artist_step2_title: 'Visualizzare i tuoi concerti',
    artist_step2_desc: 'Nella pagina Concerti puoi vedere tutti i tuoi eventi passati e futuri. Usa i filtri per status (confermato, tentativo, completato) e l\'anno per trovare rapidamente quello che cerchi.',
    artist_step2_tip1: 'Clicca su un concerto per vedere tutti i dettagli finanziari',
    artist_step2_tip2: 'Usa il calendario per una vista mensile dei tuoi impegni',
    artist_step2_tip3: 'Esporta i dati in CSV o PDF per la tua documentazione',
    artist_step3_title: 'Controllare i guadagni - My Fees',
    artist_step3_desc: "La sezione My Fees mostra il reddito netto totale come artista, diviso per anno. Vedi anche il breakdown per tipo di concerto e i clienti top (organizzatori che ti hanno prenotato di più).",
    artist_step3_tip1: 'Il reddito netto è calcolato automaticamente: ricavi - spese - commissioni',
    artist_step3_tip2: 'Controlla i "Top Organizers" per capire chi ti porta più lavoro',
    artist_step3_tip3: 'Usa i grafici per identificare trend stagionali nei tuoi guadagni',
    artist_step4_title: 'Verificare Revenue Shares - Chi ti deve soldi',
    artist_step4_desc: 'Nella pagina Revenue Shares vedi tutti i pagamenti che devi ricevere o effettuare. Ogni concerto può avere revenue shares per artisti collaboratori, booking manager, venue manager, ecc.',
    artist_step4_tip1: 'Filtra per status "Pending" per vedere chi ti deve ancora pagare',
    artist_step4_tip2: 'Aggiorna lo status a "Paid" quando ricevi il pagamento',
    artist_step4_tip3: 'Aggiungi note per tracciare metodi di pagamento e date',
    artist_step5_title: 'Usare l\'AI Assistant per domande rapide',
    artist_step5_desc: "L'AI Music Assistant risponde a domande sui tuoi dati in linguaggio naturale. Chiedi \"Quanto ho guadagnato quest'anno?\" o \"Mostrami i prossimi 3 concerti\" per ottenere risposte immediate.",
    artist_step5_tip1: 'Prova i prompt suggeriti per scoprire tutte le funzionalità',
    artist_step5_tip2: 'Puoi chiedere statistiche, elenchi concerti, analisi per venue',
    artist_step5_tip3: 'Esporta le conversazioni in PDF per reportistica',
    
    // Booking Manager guide steps
    bm_step1_title: 'Creare un nuovo concerto - Step completo',
    bm_step1_desc: 'Clicca su "Nuovo Concerto" dalla pagina Concerti. Compila tutti i campi obbligatori: titolo, data, artista (performer), organizzatore, venue. Aggiungi i dettagli finanziari come performance fee, ticket price, spese di viaggio.',
    bm_step1_tip1: 'Assicurati di selezionare te stesso o la tua agenzia come organizzatore',
    bm_step1_tip2: 'Imposta la processing fee percentage per calcolare automaticamente le commissioni',
    bm_step1_tip3: 'Salva come "Pianificato" se non ancora confermato, poi aggiorna a "Confermato"',
    bm_step2_title: 'Gestire contatti artisti e venue',
    bm_step2_desc: 'Nella pagina Contatti puoi creare e gestire il tuo database di artisti, venue manager, tecnici. Ogni contatto può avere ruolo, email, telefono, note personalizzate.',
    bm_step2_tip1: 'Aggiungi "Monthly Retainer Fee" per artisti con contratti mensili fissi',
    bm_step2_tip2: "Usa le note per tracciare preferenze dell'artista (piano, backstage, ecc.)",
    bm_step2_tip3: 'Abilita "Auto-generate tasks" per promemoria follow-up automatici',
    bm_step3_title: 'Configurare processing fee e commissioni',
    bm_step3_desc: 'La processing fee è la tua commissione come booking manager. Puoi impostarla a livello di concerto (campo "Processing Fee %") oppure a livello di contatto (organizzatore) per applicarla automaticamente.',
    bm_step3_tip1: 'La commissione si calcola sul totale ricavi (ticket sales o performance fee)',
    bm_step3_tip2: 'Imposta processing fee tra 10-20% secondo standard di mercato',
    bm_step3_tip3: 'Monitora le commissioni totali nella sezione My Fees',
    bm_step4_title: 'Tracciare le commissioni - My Fees sezione BM',
    bm_step4_desc: 'La sezione My Fees mostra le tue commissioni totali come booking manager, divise tra commissioni percentuali sui concerti e retainer fee mensili fissi da artisti.',
    bm_step4_tip1: 'Vedi il breakdown per tipo di concerto per capire dove guadagni di più',
    bm_step4_tip2: 'La sezione "Top Artists" mostra quali artisti ti generano più revenue',
    bm_step4_tip3: 'Esporta i dati per reportistica fiscale o contabilità',
    bm_step5_title: 'Report e Analytics - Export dati',
    bm_step5_desc: 'La pagina Analytics offre grafici interattivi su ricavi, spese, commissioni per periodo. Puoi esportare i dati in CSV o PDF per inviarli a clienti o commercialisti.',
    bm_step5_tip1: 'Usa i filtri per anno per confrontare performance tra periodi',
    bm_step5_tip2: 'Il grafico "Revenue vs Expenses" mostra l\'andamento finanziario nel tempo',
    bm_step5_tip3: 'Esporta la lista "Completed Concerts" per documentazione contratti',
    
    // Venue Manager guide steps
    vm_step1_title: 'Registrare un venue - Dettagli e capacità',
    vm_step1_desc: "Vai alla pagina Venues e clicca \"Nuovo Venue\". Inserisci nome, indirizzo (usa l'autocomplete per compilare automaticamente città e paese), capacità, disponibilità piano.",
    vm_step1_tip1: 'Specifica la capacità massima per calcolare correttamente i ricavi attesi',
    vm_step1_tip2: 'Indica disponibilità e modello piano per facilitare la pianificazione concerti',
    vm_step1_tip3: 'Aggiungi costi rental tipici e commissioni biglietti per trasparenza',
    vm_step2_title: 'Visualizzare concerti nel venue',
    vm_step2_desc: 'Dalla pagina Concerti, usa il filtro "Venue" per vedere tutti gli eventi programmati nella tua location. Puoi anche usare il Calendario per una vista mensile.',
    vm_step2_tip1: 'Il calendario mostra gli eventi con colori diversi per status',
    vm_step2_tip2: 'Clicca su un evento per vedere dettagli completi',
    vm_step2_tip3: 'Usa i filtri per nascondere eventi cancellati o completati',
    vm_step3_title: 'Gestire disponibilità piano',
    vm_step3_desc: 'Quando modifichi un venue, puoi specificare se è disponibile un pianoforte, il modello esatto, e eventuali costi di rental piano. Queste informazioni aiutano i booking manager nella pianificazione.',
    vm_step3_tip1: 'Indica chiaramente se il piano è incluso nel rental o ha costo separato',
    vm_step3_tip2: 'Specifica marca e modello per artisti con richieste specifiche',
    vm_step3_tip3: 'Aggiorna queste info se cambia strumentazione disponibile',
    vm_step4_title: 'Collaborare con booking managers',
    vm_step4_desc: 'I booking manager possono vedere i tuoi venue quando creano concerti. Assicurati che i tuoi contatti (email, telefono) siano aggiornati nella sezione Profilo per facilitare la comunicazione.',
    vm_step4_tip1: 'Rispondi rapidamente alle richieste per confermare disponibilità date',
    vm_step4_tip2: 'Usa le note nei concerti per comunicare requisiti tecnici',
    vm_step4_tip3: 'Tieni aggiornati i costi rental per evitare sorprese',
    
    // FAQs
    faq_title: 'Domande Frequenti',
    faq_intro: 'Risposte alle domande più comuni su Maestro Music Manager',
    faq1_q: 'Come aggiungo un nuovo concerto?',
    faq1_a: 'Vai alla pagina Concerti, clicca sul pulsante "Nuovo Concerto" in alto a destra. Compila i campi obbligatori (titolo, data, performer, organizer, venue) e salva. Puoi impostare lo status come "Tentativo" se non ancora confermato.',
    faq2_q: 'Come si calcolano le commissioni del booking manager?',
    faq2_a: 'Le commissioni si calcolano applicando la "Processing Fee %" al totale ricavi del concerto (ticket sales o performance fee). Puoi impostare questa percentuale nel campo "Processing Fee %" quando crei/modifichi un concerto, oppure salvarla direttamente nel contatto organizzatore per applicarla automaticamente.',
    faq3_q: 'Posso esportare i miei dati?',
    faq3_a: "Sì! Dalla pagina Analytics puoi esportare la lista concerti completati in formato CSV o PDF. Inoltre, nelle impostazioni profilo trovi l'opzione \"Esporta Dati\" per scaricare tutti i tuoi dati in formato JSON (GDPR compliance).",
    faq4_q: "Come funziona l'AI Music Assistant?",
    faq4_a: "L'AI Assistant è uno strumento che risponde a domande sui tuoi dati in linguaggio naturale. Puoi chiedere statistiche (\"Quanto ho guadagnato quest'anno?\"), elenchi (\"Mostrami i prossimi 3 concerti\"), analisi (\"Quale venue è più redditizio?\") e molto altro. L'AI accede ai tuoi dati autorizzati e fornisce risposte immediate.",
    faq5_q: 'Cosa sono le Revenue Shares?',
    faq5_a: 'Le Revenue Shares sono quote di guadagno da distribuire tra i vari stakeholder di un concerto (artisti collaboratori, booking manager, venue manager, promoter). Ogni concerto può avere multiple revenue shares con percentuali o importi fissi, e puoi tracciarne lo status di pagamento (Pending/Paid).',
    faq6_q: 'Posso gestire più ruoli contemporaneamente?',
    faq6_a: 'Sì! Maestro supporta utenti multi-ruolo. Ad esempio, puoi essere sia Artist che Booking Manager. Il sistema mostra automaticamente le pagine e i dati rilevanti per tutti i tuoi ruoli attivi. Le Analytics e il Dashboard aggregano i dati da tutte le tue prospettive.',
    faq7_q: 'Come collego un contatto al mio account utente?',
    faq7_a: "Quando crei o modifichi un contatto, puoi selezionare \"User Email\" dal dropdown per associarlo a un account esistente. Questo è utile quando un artista ha un account Maestro e vuoi collegare il suo contatto al suo login. L'associazione abilita scenari collaborativi come booking manager che gestiscono artisti registrati.",
    faq8_q: 'Posso duplicare un concerto?',
    faq8_a: 'Sì! Nella pagina Concerti, clicca sui tre puntini accanto a un concerto e seleziona "Duplica". Il sistema crea automaticamente un nuovo concerto con tutti i dati copiati (performer, organizer, venue, fees), data impostata a domani e "(Copy)" aggiunto al titolo. Puoi poi modificarlo rapidamente per adattarlo.',
    
    // Fan CRM
    fan_crm_description: 'Gestisci la tua fan base e il loro engagement',
    add_fan: 'Aggiungi Fan',
    edit_fan: 'Modifica Fan',
    total_fans: 'Fan Totali',
    vip_fans: 'Fan VIP',
    total_ltv: 'LTV Totale',
    email_consent_rate: 'Consenso Email',
    search_fans: 'Cerca fan...',
    filter_tier: 'Filtra per tier',
    filter_source: 'Filtra per fonte',
    filter_city: 'Filtra per città',
    all_tiers: 'Tutti i Tier',
    all_sources: 'Tutte le Fonti',
    only_consented: 'Solo con consenso',
    no_fans_found: 'Nessun fan trovato',
    no_fans_description: 'Aggiungi il tuo primo fan per iniziare a costruire il database',
    add_first_fan: 'Aggiungi il primo fan',
    concerts_attended: 'concerti',
    delete_fan_title: 'Elimina Fan',
    delete_fan_description: 'Sei sicuro di voler eliminare questo fan? Questa azione non può essere annullata.',
    basic_info: 'Info Base',
    engagement: 'Engagement',
    email: 'Email',
    first_name: 'Nome',
    last_name: 'Cognome',
    phone: 'Telefono',
    birth_date: 'Data di Nascita',
    city: 'Città',
    country: 'Paese',
    source: 'Fonte',
    fan_tier: 'Tier Fan',
    notes: 'Note',
    notes_placeholder: 'Aggiungi note su questo fan...',
    engagement_description: 'Queste metriche sono calcolate automaticamente in base alle attività del fan.',
    lifetime_value: 'Valore Lifetime',
    merchandise_spent: 'Spesa Merchandise',
    emails_opened: 'Email Aperte',
    emails_clicked: 'Email Cliccate',
    gdpr_info_title: 'Consenso GDPR',
    gdpr_info_description: 'Gestisci il consenso per le comunicazioni email e le attività di marketing in conformità con il GDPR.',
    email_consent_label: 'Comunicazioni Email',
    email_consent_description: 'Il fan ha acconsentito a ricevere comunicazioni email.',
    marketing_consent_label: 'Attività di Marketing',
    marketing_consent_description: 'Il fan ha acconsentito alle attività di marketing e promozionali.',
    consent_recorded: 'Consenso registrato il',
    // Phase 5: Attendance tracking
    add_attendance: 'Aggiungi Partecipazione',
    edit_attendance: 'Modifica Partecipazione',
    concert: 'Concerto',
    select_concert: 'Seleziona un concerto',
    ticket_type: 'Tipo Biglietto',
    ticket_price: 'Prezzo Biglietto',
    external_ticket_id: 'ID Biglietto Esterno',
    checked_in: 'Check-in Effettuato',
    checked_in_description: 'Segna se il fan ha effettuato il check-in alla venue',
    concert_attendees: 'Partecipanti Concerto',
    total_attendees: 'Totale Partecipanti',
    ticket_revenue: 'Ricavi Biglietti',
    search_attendees: 'Cerca partecipanti...',
    no_attendees_found: 'Nessun partecipante trovato per questo concerto',
    delete_attendance_title: 'Rimuovi Partecipazione',
    delete_attendance_description: 'Sei sicuro di voler rimuovere questa partecipazione?',
    no_attendance: 'Nessuna partecipazione registrata',
    
    // Access denied
    access_denied: 'Accesso Negato',
    access_denied_description: 'Non hai i permessi per accedere a questa pagina.',
    access_denied_help: 'Se pensi che sia un errore, contatta un amministratore.',
    go_to_dashboard: 'Vai alla Dashboard',
    go_back: 'Torna Indietro',
    
    // Tasks Page
    tasks_title: 'Attività',
    tasks_description: 'Gestisci le tue attività e scadenze',
    total_tasks: 'Attività Totali',
    all_tasks: 'Tutte le attività',
    past_due_date: 'Oltre la scadenza',
    currently_active: 'Attualmente attive',
    finished_tasks: 'Attività completate',
    task_board: 'Bacheca Attività',
    todo: 'Da Fare',
    no_tasks_yet: 'Nessuna attività ancora',
    create_first_task: 'Crea la tua prima attività per iniziare a organizzare il lavoro',
    add_first_task: 'Aggiungi la Tua Prima Attività',
    delete_task_confirm: 'Questa azione non può essere annullata. L\'attività verrà eliminata definitivamente.',
    assigned: 'Assegnato',
    today_label: 'Oggi',
    overdue_label: 'In Ritardo',
    
    // Expenses Page
    expenses_title: 'Spese',
    expenses_description: 'Traccia e gestisci tutte le tue spese',
    total_expenses: 'Spese Totali',
    tax_deductible: 'Detraibili',
    total_items: 'Totale Elementi',
    search_expenses: 'Cerca spese...',
    all_categories: 'Tutte le Categorie',
    delete_expense: 'Elimina Spesa',
    delete_expense_confirm: 'Sei sicuro di voler eliminare questa spesa? Questa azione non può essere annullata.',
    no_expenses_found: 'Nessuna spesa trovata',
    no_expenses_yet: 'Nessuna spesa ancora',
    start_tracking: 'Inizia a tracciare le tue spese',
    add_first_expense: 'Aggiungi la Tua Prima Spesa',
    recurring: 'Ricorrente',
    tax: 'Fisco',
    date_label: 'Data',
    title_label: 'Titolo',
    category_label: 'Categoria',
    amount_label: 'Importo',
    status_label: 'Stato',
    actions_label: 'Azioni',
    
    // MyFees Page
    my_fees_title: 'Le Mie Commissioni',
    my_fees_description: 'Traccia i tuoi guadagni e commissioni',
    booking_commissions: 'Commissioni Booking',
    from_organized_concerts: 'Da concerti organizzati',
    monthly_retainers: 'Retainer Mensili',
    annual_retainers: 'Retainer annuali',
    avg_commission: 'Commissione Media',
    per_concert: 'Per concerto',
    top_client: 'Cliente Top',
    monthly_trend: 'Andamento Mensile',
    fees_by_concert_type: 'Commissioni per Tipo Concerto',
    top_clients_by_commission: 'Top 5 Clienti per Commissione',
    monthly_retainer_contracts: 'Contratti Retainer Mensili',
    performance_fees: 'Cachet Performance',
    from_performances: 'Da performance',
    avg_fee: 'Cachet Medio',
    per_performance: 'Per performance',
    top_organizer: 'Organizzatore Top',
    monthly_performance_fees: 'Cachet Mensili',
    top_organizers: 'Top 5 Organizzatori',
    total_income: 'Reddito Totale',
    total_income_all_sources: 'Reddito Totale (Tutte le Fonti)',
    combined_earnings: 'Commissioni booking + cachet performance + retainer',
    month_label: 'mese',
    annual: 'Annuale',
    
    // Profile Page
    profile_title: 'Profilo',
    profile_description: 'Gestisci le preferenze del tuo account',
    account_information: 'Informazioni Account',
    email_and_roles: 'La tua email e i tuoi ruoli attuali',
    current_roles: 'Ruoli Attuali',
    no_roles_assigned: 'Nessun ruolo assegnato',
    personal_information: 'Informazioni Personali',
    update_personal_details: 'Aggiorna i tuoi dati personali',
    full_name: 'Nome Completo',
    your_full_name: 'Il tuo nome completo',
    your_company_name: 'Il nome della tua azienda',
    preferences: 'Preferenze',
    customize_settings: 'Personalizza le impostazioni dell\'applicazione',
    language_label: 'Lingua',
    timezone_label: 'Fuso Orario',
    currency_label: 'Valuta',
    save_personal_info: 'Salva Info Personali',
    onboarding_section: 'Onboarding',
    restart_checklist: 'Riavvia la checklist di avvio',
    reset_onboarding: 'Resetta Checklist Onboarding',
    resetting: 'Resettando...',
    no_contact_associated: 'Nessun contatto associato. Chiedi a un admin di collegare il tuo account a un contatto.',
    profile_updated: 'Profilo aggiornato con successo',
    profile_update_error: 'Errore nell\'aggiornamento del profilo',
    personal_info_updated: 'Informazioni personali aggiornate con successo',
    personal_info_error: 'Errore nell\'aggiornamento delle informazioni personali',
    onboarding_reset_success: 'Checklist onboarding resettata e apparirà sulla dashboard',
    onboarding_reset_error: 'Errore nel reset della checklist onboarding',
    
    // Settings Page
    settings_title: 'Impostazioni',
    settings_description: 'Gestisci le preferenze di account e privacy',
    privacy_data_management: 'Privacy e Gestione Dati',
    privacy_policy: 'Informativa Privacy',
    terms_of_service: 'Termini di Servizio',
    app_update_management: 'Gestione Aggiornamenti App',
    send_update_to_all: 'Invia aggiornamento a tutti gli utenti',
    send_update_description: 'Forza tutti gli utenti connessi a ricaricare l\'app con l\'ultima versione.',
    update_sent_success: 'Notifica di aggiornamento inviata a tutti gli utenti connessi',
    update_sent_error: 'Errore nell\'invio della notifica di aggiornamento',
    sending_update: 'Invio...',
    contact_privacy_team: 'Contatta il Team Privacy',
    upgrade_success: 'Upgrade completato con successo! Benvenuto nel tuo nuovo piano.',
    
    // RevenueShares Page
    revenue_shares_title: 'Quote Ricavi',
    revenue_shares_description: 'Gestisci la distribuzione dei ricavi tra collaboratori e artisti',
    total_revenue_shares: 'Totale Quote Ricavi',
    all_collaborations: 'Tutte le collaborazioni',
    pending_payments: 'Pagamenti in Sospeso',
    to_calculate_pay: 'Da calcolare/pagare',
    paid_shares: 'Quote Pagate',
    total_to_pay: 'Totale da Pagare',
    calculated_shares: 'Quote calcolate',
    to_configure: 'Da Configurare',
    concerts_without_shares: 'Concerti senza quote',
    filters: 'Filtri',
    search_concert_collaborator: 'Cerca per concerto o collaboratore...',
    filter_by_status: 'Filtra per stato',
    all_statuses: 'Tutti gli stati',
    pending: 'In Attesa',
    calculated: 'Calcolato',
    paid: 'Pagato',
    disputed: 'Contestato',
    filter_by_type: 'Filtra per tipo',
    all_types_shares: 'Tutti i tipi',
    revenue_distribution: 'Distribuzione Ricavi',
    beneficiary: 'Beneficiario',
    share_type: 'Tipo',
    percentage: 'Percentuale',
    fixed_amount: 'Importo Fisso',
    calculation_base: 'Base di Calcolo',
    calculated_amount: 'Importo',
    calculate: 'Calcola',
    mark_as_paid: 'Segna come Pagato',
    bulk_actions: 'Azioni Multiple',
    selected: 'selezionati',
    calculate_selected: 'Calcola Selezionati',
    mark_selected_paid: 'Segna come Pagate',
    deselect_all: 'Deseleziona',
    no_revenue_shares: 'Nessuna quota ricavi ancora',
    configure_first_share: 'Le quote verranno create automaticamente dai concerti',
    configure: 'Configura',
    internal_user: 'Utente interno',
    unknown: 'Sconosciuto',
    concerts_without_collaborators: 'Concerti Senza Collaboratori',
    export_completed: 'Export completato',
    collaborations_exported: 'Le collaborazioni sono state esportate con successo',
    calculation_completed: 'Calcolo completato - L\'importo è stato calcolato con successo',
    collaborations_marked_paid: 'collaborazioni segnate come pagate',
    collaborations_calculated: 'collaborazioni calcolate',
    no_collaboration_found: 'Nessuna collaborazione trovata',
    modify_filters: 'Prova a modificare i filtri',
    
    // Calendar Page (additional keys)
    artistic_projects_label: 'Progetti Artistici',
    concert_rescheduled: 'Concerto riprogrammato',
    failed_reschedule: 'Errore nel riprogrammare il concerto',
    concert_deleted_success: 'Concerto eliminato con successo',
    no_permission_delete: 'Non hai i permessi per eliminare questo concerto',
    
    // Analytics Page
    analytics_title: 'Analisi',
    analytics_description: 'Analisi dettagliata delle performance e dei ricavi',
    failed_load_analytics: 'Errore nel caricamento delle analisi. Riprova.',
    no_concerts_for_filters: 'Nessun concerto trovato per i filtri selezionati',
    gross_volume: 'Volume Lordo',
    total_revenue_label: 'Totale ricavi',
    direct_expenses_label: 'Spese Dirette',
    total_expenses_label: 'Totale spese',
    revenue_shares_kpi: 'Quote Ricavo',
    collaborators_label: 'Collaboratori',
    final_earnings: 'Guadagno finale',
    total_commissions: 'Totale Commissioni',
    booking_manager_commissions: 'Commissioni booking manager',
    revenue_breakdown: 'Ripartizione Ricavi',
    expense_breakdown: 'Ripartizione Spese',
    loading_profile: 'Caricamento profilo...',
    access_denied_roles: 'Non hai i permessi necessari per visualizzare questa sezione. Richiesto uno dei seguenti ruoli: Admin, Artist, Booking Manager, Accountant.',
    custom_view_description: 'Stai visualizzando i concerti dove sei',
    multi_role_description: 'multi-ruolo: vedi tutti i concerti che ti riguardano',
    viewing_organizer_only: 'Stai visualizzando solo i concerti dove sei organizer.',
    viewing_performer_only: 'Stai visualizzando solo i concerti dove sei performer.',
    viewing_vm_only: 'Stai visualizzando solo i concerti nelle tue venue.',
    viewing_relevant: 'Stai visualizzando solo i concerti che ti riguardano.',
    all_filter: 'Tutti',
    completed_filter: 'Completati',
    confirmed_filter: 'Confermati',
    export_pdf: 'Esporta PDF',
    net_income_trend: 'Andamento Reddito Netto',
    net_income_label: 'Reddito Netto',
    revenue_share_analysis: 'Analisi Quote Ricavo',
    revenue_shares_by_type: 'Quote Ricavo per Tipo',
    top_share_recipients: 'Top Destinatari Quote',
    concert_analysis: 'Analisi Concerti',
    concert_status_distribution: 'Distribuzione Concerti per Stato',
    profitability_by_type: 'Redditività per Tipo',
    monthly_audience_growth: 'Crescita Pubblico Mensile',
    concerts_by_venue_type: 'Concerti per Tipo Venue',
    top_venues_by_revenue: 'Top Venue per Ricavi',
    top_organizers_by_commission: 'Top Organizzatori per Commissioni',
    top_artists_by_revenue: 'Top Artists per Ricavi',
    top_booking_managers_by_commission: 'Top Booking Managers per Commissioni',
    concerts_by_country: 'Concerti per Paese',
    concerts_by_region: 'Concerti per Regione (Italia)',
    artistic_project_performance: 'Performance per Progetto Artistico',
    revenue_by_project: 'Ricavi per Progetto',
    concerts_by_project: 'Concerti per Progetto',
    artistic_project_details: 'Dettaglio Progetti Artistici',
    project_label: 'Progetto',
    total_concerts_label: 'Concerti',
    total_audience_label: 'Audience Totale',
    avg_revenue_per_concert: 'Media Ricavi/Concerto',
    avg_audience_per_concert: 'Media Audience/Concerto',
    concert_list: 'Elenco Concerti',
    completed_concerts: 'Concerti Completati',
    revenue_table_label: 'Ricavi',
    commission_table_label: 'Commissione',
    cachet_label: 'Cachet',
    tickets_label: 'Biglietti',
    merchandise_label: 'Merchandising',
    travel_label: 'Viaggio',
    accommodation_label: 'Alloggio',
    piano_rental_label: 'Noleggio Pianoforte',
    other_label: 'Altro',
    commissions_label: 'Commissioni',
    verifying_credentials: 'Verifica credenziali in corso...',
    loading_data: 'Caricamento dati...',
    
    // Purchase Intelligence
    purchase_intelligence: 'Purchase Intelligence',
    total_revenue: 'Revenue Totale',
    avg_order_value: 'Ordine Medio',
    gap_revenue: 'Revenue Potenziale',
    conversion_rate: 'Tasso Conversione',
    missing_product: 'Prodotto Mancante',
    all_products: 'Tutti i prodotti',
    tier: 'Tier',
    purchased: 'Prodotti',
    missing: 'Mancanti',
    missing_products: 'Prodotti mancanti',
    orders: 'Ordini',
    purchased_products: 'Prodotti acquistati',
    have_not_purchased: 'non hanno acquistato',
    create_segment: 'Crea Segmento',
    add_offline_purchase: 'Aggiungi Acquisto',
    register_offline_purchase: 'Registra Acquisto Offline',
    register_offline_purchase_description: 'Registra un acquisto effettuato offline per tenere traccia completa.',
    product: 'Prodotto',
    quantity: 'Quantità',
    unit_price: 'Prezzo Unitario',
    purchase_date: 'Data Acquisto',
    save_purchase: 'Salva Acquisto',
    add_purchase: 'Aggiungi acquisto',
    view_details: 'Vedi dettagli',
    product_matrix: 'Matrice Prodotti',
    no_products_available: 'Nessun prodotto disponibile',
    no_results: 'Nessun risultato trovato',

    // Tasks page (extended)
    tasks_page_description: 'Gestisci le attività del team e personali',
    new_task: 'Nuova attività',
    no_tasks_short: 'Nessuna attività',
    create_first_task_short: 'Crea la tua prima attività per iniziare',
    create_task_short: 'Crea attività',
    stat_total: 'Totali',
    stat_overdue: 'In ritardo',
    stat_in_progress: 'In corso',
    stat_completed: 'Completate',
    board_todo: 'Da fare',
    board_in_progress: 'In corso',
    board_completed: 'Completate',
    board_cancelled: 'Annullate',
    delete_task: 'Elimina attività',
    delete_task_description: "Sei sicuro di voler eliminare questa attività? L'azione non può essere annullata.",
    edit_task: 'Modifica attività',
    form_title: 'Titolo',
    form_description: 'Descrizione',
    form_category: 'Categoria',
    form_status: 'Stato',
    form_priority: 'Priorità',
    form_assigned_to: 'Assegnato a',
    form_linked_contact: 'Contatto collegato',
    form_due_date: 'Scadenza',
    form_reminder: 'Promemoria',
    reminder_hint: 'Quando ricordarti di questa attività',
    pick_date: 'Scegli una data',
    select_category_placeholder: 'Seleziona una categoria',
    select_user_placeholder: 'Seleziona utente',
    unassigned_label: 'Non assegnato',
    select_contact_placeholder: 'Seleziona contatto',
    no_contact_option: 'Nessun contatto',
    update_task: 'Aggiorna attività',
    create_task_action: 'Crea attività',
    category_followup: 'Follow-up',
    category_contract: 'Contratto',
    category_payment: 'Pagamento',
    category_planning: 'Pianificazione',
    category_other: 'Altro',
    priority_low: 'Bassa',
    priority_medium: 'Media',
    priority_high: 'Alta',
    priority_urgent: 'Urgente',
    status_todo: 'Da fare',
    status_in_progress: 'In corso',
    status_completed: 'Completato',
    status_cancelled: 'Annullato',
    task_actions: 'Azioni attività',
    move_to: 'Sposta in',
    created_by_label: 'Creato da',
    csv_col_title: 'Titolo',
    csv_col_description: 'Descrizione',
    csv_col_category: 'Categoria',
    csv_col_status: 'Stato',
    csv_col_priority: 'Priorità',
    csv_col_due_date: 'Scadenza',
    csv_col_assignee: 'Assegnatario',
    mark_completed: 'Segna come completato',
    mark_todo: 'Segna come da fare',
  },
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('maestro-language');
    return (saved === 'en' || saved === 'it') ? saved : 'it';
  });

  useEffect(() => {
    localStorage.setItem('maestro-language', language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};